import React, { useState } from 'react';
import { useCustom, useCustomMutation, useNotification } from '@refinedev/core';
import {
  Input,
  Button,
  Card,
  Space,
  Tag,
  Spin,
  Empty,
  Form,
  Select,
  InputNumber,
  Modal,
  Divider,
  Row,
  Col,
  theme,
} from 'antd';
import { LocalizedContentTabs, normalizeTranslations } from '../components/LocalizedContentTabs';
import { SearchOutlined, ImportOutlined, GlobalOutlined } from '@ant-design/icons';
import {
  UsdaSearchResponseDto,
  IngredientSearchResultDto,
  IngredientAmountUnit,
  AddUsdaToCatalogDto,
} from '@wellverse/shared-ts';

const { Search } = Input;

const LANG_FIELDS = [
  { key: 'name', label: 'Name', placeholder: 'Translated name', required: true },
];

const NutritionBadge: React.FC<{ label: string; value: number; unit: string }> = ({
  label,
  value,
  unit,
}) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        background: token.colorBgContainer,
        borderRadius: 8,
        padding: '8px 12px',
        textAlign: 'center',
        minWidth: 80,
      }}
    >
      <div style={{ color: '#3ecf8e', fontSize: 16, fontWeight: 600 }}>
        {value ?? 0}
      </div>
      <div style={{ color: token.colorTextSecondary, fontSize: 10, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ color: token.colorTextSecondary, fontSize: 10 }}>{unit}</div>
    </div>
  );
};

export const UsdaSearch: React.FC = () => {
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  const [importingItem, setImportingItem] = useState<IngredientSearchResultDto | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const { open } = useNotification();

  const {
    data: searchData,
    isFetching: searchFetching,
    isError: searchError,
  } = useCustom<UsdaSearchResponseDto>({
    url: '/ingredient-catalog/search-usda',
    method: 'get',
    config: {
      query: {
        query: query || undefined,
        usdaLimit: 10,
        catalogLimit: 20,
      },
    },
    queryOptions: {
      enabled: !!query,
      keepPreviousData: false,
    },
  });

  const { mutate: importUsda, isLoading: importLoading } = useCustomMutation();

  const handleSearch = (value: string) => {
    if (value.trim()) {
      setQuery(value.trim());
    }
  };

  const handleImport = (values: any) => {
    if (!importingItem?.usdaItem) return;

    const payload: AddUsdaToCatalogDto = {
      fdcId: importingItem.usdaItem.fdcId,
      name: values.name,
      translations: normalizeTranslations(values.translations, LANG_FIELDS) ?? undefined,
      category: values.category,
      defaultAmountUnit: values.defaultAmountUnit,
      defaultAmountGrams: values.defaultAmountGrams,
      commonAllergens: values.commonAllergens || [],
      tags: values.tags || [],
    };

    importUsda(
      {
        url: '/ingredient-catalog/from-usda',
        method: 'post',
        values: payload,
      },
      {
        onSuccess: () => {
          open?.({ type: 'success', message: 'Imported successfully' });
          setIsModalOpen(false);
          setImportingItem(null);
          form.resetFields();
        },
        onError: (err: any) => {
          open?.({
            type: 'error',
            message: err?.response?.data?.message || 'Import failed',
          });
        },
      },
    );
  };

  const openImportModal = (item: IngredientSearchResultDto) => {
    setImportingItem(item);
    const usda = item.usdaItem!;
    form.setFieldsValue({
      name: usda.description,
      category: '',
      defaultAmountUnit: IngredientAmountUnit.GRAM,
      defaultAmountGrams: 100,
      commonAllergens: [],
      tags: [],
    });
    setIsModalOpen(true);
  };

  const raw = searchData as any;
  const results: UsdaSearchResponseDto | undefined = raw?.results
    ? raw
    : raw?.data;

  const catalogItems = results?.results?.filter((r: any) => r.source === 'catalog') ?? [];
  const usdaItems = results?.results?.filter((r: any) => r.source === 'usda') ?? [];
  const hasResults = results !== undefined;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: token.colorText, marginBottom: 8 }}>
          <GlobalOutlined style={{ marginRight: 8, color: '#3ecf8e' }} />
          USDA FoodData Central Search
        </h2>
        <p style={{ color: token.colorTextSecondary }}>
          Search the USDA database and import foods into the local ingredient catalog.
        </p>
      </div>

      <Search
        placeholder="Search USDA foods (e.g. chicken breast, broccoli, brown rice...)"
        enterButton={<Button icon={<SearchOutlined />}>Search</Button>}
        size="large"
        onSearch={handleSearch}
        loading={searchFetching}
        style={{ marginBottom: 24 }}
      />

      {searchFetching && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {!searchFetching && !query && (
        <Empty description="Type a query and press Enter to search" />
      )}

      {!searchFetching && searchError && (
        <Empty description="Search failed. Please try again." />
      )}

      {!searchFetching && query && !hasResults && (
        <Empty description="No results found" />
      )}

      {!searchFetching && hasResults && (
        <>
          {catalogItems.length > 0 && (
            <>
              <h3 style={{ color: token.colorText, marginBottom: 16 }}>
                Local Catalog Matches ({results?.totalCatalogMatches ?? 0})
              </h3>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {catalogItems.map((item: any, idx: number) => (
                  <Col xs={24} md={12} lg={8} key={`catalog-${idx}`}>
                    <Card
                      size="small"
                      style={{
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder,
                      }}
                      title={
                        <Space>
                          <Tag color="#3ecf8e">Local</Tag>
                          <span style={{ color: token.colorText }}>
                            {item.catalogItem?.name}
                          </span>
                        </Space>
                      }
                    >
                      <div style={{ color: token.colorTextSecondary, fontSize: 12, marginBottom: 8 }}>
                        {item.catalogItem?.category} · {item.catalogItem?.defaultAmountGrams}g
                      </div>
                      <Space wrap>
                        <NutritionBadge
                          label="Cal"
                          value={item.catalogItem?.caloriesPer100g ?? 0}
                          unit="kcal"
                        />
                        <NutritionBadge
                          label="Protein"
                          value={item.catalogItem?.proteinPer100g ?? 0}
                          unit="g"
                        />
                        <NutritionBadge
                          label="Fat"
                          value={item.catalogItem?.fatPer100g ?? 0}
                          unit="g"
                        />
                        <NutritionBadge
                          label="Carbs"
                          value={item.catalogItem?.carbsPer100g ?? 0}
                          unit="g"
                        />
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {usdaItems.length > 0 && (
            <>
              <Divider style={{ borderColor: token.colorBorder }} />
              <h3 style={{ color: token.colorText, marginBottom: 16 }}>
                USDA FoodData Central Matches ({results?.totalUsdaMatches ?? 0})
              </h3>
              <Row gutter={[16, 16]}>
                {usdaItems.map((item: any, idx: number) => (
                  <Col xs={24} md={12} lg={8} key={`usda-${idx}`}>
                    <Card
                      size="small"
                      style={{
                        background: token.colorBgContainer,
                        borderColor: token.colorBorder,
                      }}
                      title={
                        <Space>
                          <Tag color="#f5a623">USDA</Tag>
                          <span style={{ color: token.colorText }}>
                            {item.usdaItem?.description}
                          </span>
                        </Space>
                      }
                      extra={
                        <Button
                          type="primary"
                          size="small"
                          icon={<ImportOutlined />}
                          onClick={() => openImportModal(item)}
                        >
                          Import
                        </Button>
                      }
                    >
                      <div style={{ color: token.colorTextSecondary, fontSize: 12, marginBottom: 8 }}>
                        FDC ID: {item.usdaItem?.fdcId} · {item.usdaItem?.dataType}
                      </div>
                      <Space wrap>
                        <NutritionBadge
                          label="Cal"
                          value={item.usdaItem?.caloriesPer100g ?? 0}
                          unit="kcal"
                        />
                        <NutritionBadge
                          label="Protein"
                          value={item.usdaItem?.proteinPer100g ?? 0}
                          unit="g"
                        />
                        <NutritionBadge
                          label="Fat"
                          value={item.usdaItem?.fatPer100g ?? 0}
                          unit="g"
                        />
                        <NutritionBadge
                          label="Carbs"
                          value={item.usdaItem?.carbsPer100g ?? 0}
                          unit="g"
                        />
                        {item.usdaItem?.fiberPer100g !== undefined && (
                          <NutritionBadge
                            label="Fiber"
                            value={item.usdaItem.fiberPer100g}
                            unit="g"
                          />
                        )}
                        {item.usdaItem?.sodiumPer100g !== undefined && (
                          <NutritionBadge
                            label="Sodium"
                            value={item.usdaItem.sodiumPer100g}
                            unit="mg"
                          />
                        )}
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {catalogItems.length === 0 && usdaItems.length === 0 && (
            <Empty description="No results found" />
          )}
        </>
      )}

      <Modal
        title="Import USDA Food to Catalog"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setImportingItem(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={importLoading}
        okText="Import"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleImport}>
          <LocalizedContentTabs fields={LANG_FIELDS} />
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. proteins, vegetables, grains, dairy, fruits" />
          </Form.Item>
          <Form.Item
            label="Default Amount Unit"
            name="defaultAmountUnit"
            rules={[{ required: true }]}
          >
            <Select>
              {Object.values(IngredientAmountUnit).map((u) => (
                <Select.Option key={u} value={u}>
                  {u}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Default Amount (g)"
            name="defaultAmountGrams"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Common Allergens" name="commonAllergens">
            <Select mode="tags" placeholder="e.g. gluten, dairy, nuts" />
          </Form.Item>
          <Form.Item label="Tags" name="tags">
            <Select mode="tags" placeholder="e.g. organic, lean, fresh" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
